import { Subject } from 'rxjs';
import generateGUID from 'utils/guidGenerator';

// Shared across every createDropdownMenu() instance on the page, so opening one closes any
// other dropdown menu that's currently open — "only one dropdown should be open at a time"
const openInstances = new Set();

/**
 * Generic open/close/select wiring for a dropdown-style widget. Builds no markup of its own —
 * `element` must already contain, by convention:
 *   - `[data-dropdown-toggle]` (optional) — click toggles the menu open/closed.
 *   - `[data-dropdown-focus]` (optional) — focus opens the menu (for typeable trigger fields,
 *     e.g. a combobox input, where a click already both focuses and would otherwise double-fire
 *     against a toggle handler).
 *   - `[data-dropdown-list]` — the options container, shown/hidden via the `hidden` attribute.
 *   - one or more `[data-dropdown-option]`, each with a `data-value` — clicking one closes the
 *     menu and emits its value.
 *
 * This follows the disclosure pattern, not the listbox one: the list is a plain list of buttons
 * (no `role="listbox"`/`"option"` — an option can't contain a focusable button, and this component
 * has no listbox keyboard model), reached with Tab. Every trigger (toggle and focus trigger) gets
 * `aria-controls` pointing at the list (which is given an id if it has none); triggers that are a
 * `<button>` also get an `aria-expanded` that mirrors the open state — it isn't valid on the plain
 * text input a focus trigger usually is.
 *
 * The menu closes on an outside click, Escape, a scroll of an ancestor (when positioned `fixed`)
 * and when focus moves to something outside `element`.
 *
 * @param {HTMLElement} element
 * @returns {{ open: Function, close: Function, getObservable: () => import('rxjs').Observable, destroy: Function }}
 */
export default function createDropdownMenu(element) {
  const toggle = element.querySelector('[data-dropdown-toggle]');
  const focusTrigger = element.querySelector('[data-dropdown-focus]');
  const list = element.querySelector('[data-dropdown-list]');
  const value$ = new Subject();
  const triggers = [toggle, focusTrigger].filter(Boolean);
  const expandableTriggers = triggers.filter((trigger) => trigger.tagName === 'BUTTON');

  if (list) {
    list.id = list.id || `dropdown-list-${generateGUID()}`;
    triggers.forEach((trigger) => trigger.setAttribute('aria-controls', list.id));
    expandableTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  }

  function setExpanded(isExpanded) {
    expandableTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', String(isExpanded)));
  }

  // Positioning is entirely CSS — this only gives the field and its menu a shared, unique
  // anchor name, once, at creation. Where CSS anchor positioning is supported, the stylesheet
  // (`@supports (anchor-name: --a)`) pins the menu to its field as `position: fixed`, which the
  // browser re-evaluates on layout (resize included) with no geometry or listeners here; a
  // `fixed` menu also never counts toward an ancestor's scrollable overflow, unlike an absolutely
  // positioned one, which is what added a second scrollbar when a menu opened inside the modal's
  // scrollable body. Elsewhere those inline declarations are simply ignored and the stylesheet's
  // `position: absolute` baseline applies. Names are per-instance so each menu binds to its own
  // field whatever the DOM order (a repeated name resolves to the last matching element that
  // precedes the menu in tree order). A GUID rather than a counter, so there's no module state.
  const anchorId = `--dropdown-anchor-${generateGUID()}`;
  element.style.setProperty('anchor-name', anchorId);
  list?.style.setProperty('position-anchor', anchorId);

  function close() {
    openInstances.delete(close);
    element.removeAttribute('data-dropdown-open');
    list?.setAttribute('hidden', '');
    setExpanded(false);
  }

  function open() {
    openInstances.forEach((closeOther) => {
      if (closeOther !== close) {
        closeOther();
      }
    });
    openInstances.add(close);
    element.setAttribute('data-dropdown-open', '');
    list?.removeAttribute('hidden');
    setExpanded(true);
  }

  function onToggleClick() {
    if (element.hasAttribute('data-dropdown-open')) {
      close();
    } else {
      open();
    }
  }

  function onListClick(event) {
    const option = event.target.closest('[data-dropdown-option]');
    if (!option) {
      return;
    }

    close();
    value$.next(option.dataset.value);
  }

  function onDocumentClick(event) {
    if (!element.contains(event.target)) {
      close();
    }
  }

  // Only closes for a real move to another element: `relatedTarget` is null when focus goes to
  // nothing focusable (a click on the list's padding or scrollbar, or the window losing focus), and
  // those are covered by the click handler or should leave the menu as it is. Moving between the
  // input, the toggle and the options stays inside `element`, so it doesn't close either.
  function onFocusOut(event) {
    if (event.relatedTarget && !element.contains(event.relatedTarget)) {
      close();
    }
  }

  function onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      close();
    }
  }

  // Capture phase: scroll events on a nested scrollable ancestor (e.g. the modal body) don't
  // bubble, but they do fire during capture, so this still sees them. A `position: fixed` menu
  // doesn't move with a scrolling ancestor, so it closes rather than stay stranded away from its
  // trigger. The `position: absolute` baseline scrolls with its content instead, and closing it
  // there would put the part of a long menu that overflows the scroll area out of reach.
  function onScroll(event) {
    if (!list || list.hasAttribute('hidden') || list.contains(event.target)) {
      return;
    }

    if (getComputedStyle(list).position === 'fixed') {
      close();
    }
  }

  const listeners = [];
  function addListener(target, type, handler, options) {
    target?.addEventListener(type, handler, options);
    listeners.push({ target, type, handler, options });
  }

  addListener(toggle, 'click', onToggleClick);
  addListener(focusTrigger, 'focus', open);
  addListener(list, 'click', onListClick);
  addListener(element, 'focusout', onFocusOut);
  addListener(document, 'click', onDocumentClick);
  addListener(document, 'keydown', onDocumentKeydown);
  addListener(window, 'scroll', onScroll, true);

  return {
    open,
    close,
    getObservable: () => value$.asObservable(),
    destroy() {
      close();
      listeners.forEach(({ target, type, handler, options }) => target?.removeEventListener(type, handler, options));
    },
  };
}
